/**
 * PasswordStrengthIndicator Component
 *
 * Visual indicator showing password strength based on multiple criteria.
 * Shows which requirements are met/unmet for immediate feedback.
 */

import React, { useMemo } from 'react';

interface PasswordRequirement {
  key: string;
  label: string;
  validator: (password: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    key: 'length',
    label: 'At least 10 characters',
    validator: (pw) => pw.length >= 10,
  },
  {
    key: 'uppercase',
    label: 'One uppercase letter',
    validator: (pw) => /[A-Z]/.test(pw),
  },
  {
    key: 'lowercase',
    label: 'One lowercase letter',
    validator: (pw) => /[a-z]/.test(pw),
  },
  {
    key: 'digit',
    label: 'One digit',
    validator: (pw) => /\d/.test(pw),
  },
  {
    key: 'special',
    label: 'One special character (!@#$%^&*...)',
    validator: (pw) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(pw),
  },
];

export interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

type StrengthLevel = 'weak' | 'fair' | 'good' | 'strong';

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  className = '',
}) => {
  const results = useMemo(() => {
    return PASSWORD_REQUIREMENTS.map((req) => ({
      ...req,
      met: req.validator(password),
    }));
  }, [password]);

  const metCount = results.filter((r) => r.met).length;
  const totalCount = PASSWORD_REQUIREMENTS.length;

  // Calculate strength level
  const getStrengthLevel = (): StrengthLevel => {
    if (metCount <= 1) return 'weak';
    if (metCount <= 2) return 'fair';
    if (metCount <= 4) return 'good';
    return 'strong';
  };

  const strengthLevel = getStrengthLevel();

  const strengthColors: Record<StrengthLevel, string> = {
    weak: 'var(--color-error)',
    fair: '#f59e0b',
    good: '#3b82f6',
    strong: 'var(--color-success)',
  };

  const strengthLabels: Record<StrengthLevel, string> = {
    weak: 'Weak',
    fair: 'Fair',
    good: 'Good',
    strong: 'Strong',
  };

  // Don't show anything if password is empty
  if (!password) {
    return null;
  }

  return (
    <div className={className} style={{ marginTop: 'var(--space-3)' }}>
      {/* Strength bar */}
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-2)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Password Strength
          </span>
          <span
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              fontWeight: 600,
              color: strengthColors[strengthLevel],
            }}
          >
            {strengthLabels[strengthLevel]}
          </span>
        </div>

        {/* Strength bar segments */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-1)',
            height: '8px',
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => {
            const isActive = index < Math.ceil((metCount / totalCount) * 4);
            return (
              <div
                key={index}
                style={{
                  flex: 1,
                  backgroundColor: isActive ? strengthColors[strengthLevel] : '#e5e5e5',
                  border: 'var(--border-thin)',
                  transition: 'background-color var(--transition-base)',
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Requirements checklist */}
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-1)',
        }}
      >
        {results.map((req) => (
          <li
            key={req.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: req.met ? 'var(--color-success)' : 'var(--color-muted)',
              transition: 'color var(--transition-base)',
            }}
          >
            <span
              style={{
                width: '16px',
                height: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: 'var(--text-xs)',
              }}
            >
              {req.met ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                </svg>
              )}
            </span>
            {req.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Utility function to validate password meets all requirements
 * Can be imported and used for form validation
 */
export function validatePasswordStrength(password: string): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  for (const req of PASSWORD_REQUIREMENTS) {
    if (!req.validator(password)) {
      errors.push(req.label);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export default PasswordStrengthIndicator;
